import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getReportableEventCounts } from '../../../api/v2/reportable_events';

const EMPTY_COUNTS = { outstandingCount: null, accountEventCount: null };

const ReportableEventCountsContext = createContext({ ...EMPTY_COUNTS, refresh: () => {}, applyCounts: () => {} });

export const useReportableEventCounts = () => useContext(ReportableEventCountsContext);

// The nav badge is shown on every clinical page, so the counts live above the routes rather than
// inside the Reportable Events page. A failed refresh keeps the last known count, and a slower
// earlier response cannot overwrite a newer one.
export default function ReportableEventCountsProvider({ children }) {
  const [counts, setCounts] = useState(EMPTY_COUNTS);
  const requestId = useRef(0);

  const applyCounts = useCallback((data) => {
    if (data == null || data.outstanding_count == null) return;

    setCounts((current) => ({
      outstandingCount: data.outstanding_count,
      accountEventCount: data.account_event_count == null ? current.accountEventCount : data.account_event_count,
    }));
  }, []);

  const refresh = useCallback(() => {
    const id = requestId.current + 1;
    requestId.current = id;

    return getReportableEventCounts()
      .then((data) => {
        if (id !== requestId.current) return;
        applyCounts(data);
      })
      .catch(() => {
        // Leave the last known count in place. A blank badge would look like there is nothing to review.
      });
  }, [applyCounts]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(() => ({ ...counts, refresh, applyCounts }), [counts, refresh, applyCounts]);

  return <ReportableEventCountsContext.Provider value={value}>{children}</ReportableEventCountsContext.Provider>;
}
